import { AgenturNameCell } from 'app/components/shared/AgenturName.cell';
import { Table } from 'app/components/table/table';
import { AggregrationType, ITableColumn, tableDividerColumnWithAgg, TableModel } from 'app/components/table/table.model';
import { useStore } from 'app/context';
import { observer } from 'mobx-react';
import { useEffect, useMemo, useState } from 'react';
import { AgtFitnessVm } from 'app/stores/ui/agt.fitness.ui.store';
import { Icon } from 'app/components/common/Icon';
import { simpleFitCell } from './FitnessTrackerListe';
import { FitnessTrackerGraphics } from './FitnessTrackerGraphics';
import { useHistoryWithQuery } from 'app/hooks/useHistoryWithQuery';
import { useLocation } from 'react-router';
import routes from 'routes';
import { formatPercentWithIcon } from 'app/utils';
import Config from 'Config';
import { AkumaHistoryChart } from './FitnessTrackerAkumaHistory';

export const FitnessTrackerAkuma = observer(() => {
	const { berichteUiiStore, agtFitnessUiStore, agenturListUiStore } = useStore();
	const [hasItems, setHasItems] = useState(false);
	const [filteredAgtIds, setFilteredAgtIds] = useState<number[]>([]);

	const [selectedAgtId, setSelectedAgtId] = useState<number | undefined>(undefined);
	const history = useHistoryWithQuery();
	const location = useLocation();

	const tm = useMemo(() => {
		const tm = new TableModel<AgtFitnessVm, number>();
		const cols: ITableColumn<AgtFitnessVm, number>[] = [];

		cols.push({
			label: 'Agentur',
			path: 'agt.name',
			sortBy: 'agt.name',
			render: AgenturNameCell,
		});

		//  --- PLANER ---
		cols.push({
			label: 'AKUMA Planer Wochen Anzahl',
			path: `planerAnzahlWochenNutzung.ze`,
			sortBy: `planerAnzahlWochenNutzung.ze`,
			cellModifier: 'border-left-yellow bg-yellow',
			labelModifier: 'border-left-yellow bg-yellow',
			render(row) {
				return simpleFitCell(row.data.planerAnzahlWochenNutzung);
			},
			agg: {
				aggType: AggregrationType.avg,
				format: (val) => formatPercentWithIcon(val, 1),
			},
		});
		cols.push({
			label: 'AKUMA Planer Übertragungsquote',
			path: `planerUebertragungquoteAmisNow.ze`,
			sortBy: `planerUebertragungquoteAmisNow.ze`,
			cellModifier: 'bg-yellow',
			labelModifier: 'bg-yellow',
			render(row) {
				return simpleFitCell(row.data.planerUebertragungquoteAmisNow);
			},
			agg: {
				aggType: AggregrationType.avg,
				format: (val) => formatPercentWithIcon(val, 1),
			},
		});

		// --- PRIME ---

		cols.push(tableDividerColumnWithAgg);
		cols.push({
			label: 'AKUMA Prime Nutzer',
			path: `isAkumaPrimeUser`,
			sortBy: `isAkumaPrimeUser`,
			cellModifier: 'bg-yellow',
			labelModifier: 'bg-yellow',
			render(row) {
				return <div className="has-text-centered">{row.data.isAkumaPrimeUser ? <Icon iconClass="check-circle" /> : <></>}</div>;
			},
			agg: {
				aggType: AggregrationType.sum,
			},
		});
		if (Config.showFitnessKpi25) {
			cols.push({
				label: 'AKUMA Prime Kundenkontaktquote',
				path: `kundenkontaktquote.ze`,
				sortBy: `kundenkontaktquote.ze`,
				cellModifier: 'border-left-yellow bg-yellow',
				labelModifier: 'border-left-yellow bg-yellow',
				render(row) {
					return simpleFitCell(row.data.primeKundenKontaktQuote);
				},
				agg: {
					aggType: AggregrationType.avg,
					format: (val) => formatPercentWithIcon(val, 1),
				},
			});
		}
		cols.push({
			label: 'AKUMA Prime Einordnungsquote',
			path: `primeEinordnungsquote.ze`,
			sortBy: `primeEinordnungsquote.ze`,
			cellModifier: 'border-left-yellow bg-yellow',
			labelModifier: 'border-left-yellow bg-yellow',
			render(row) {
				return simpleFitCell(row.data.primeEinordnungsquote);
			},
			agg: {
				aggType: AggregrationType.avg,
				format: (val) => formatPercentWithIcon(val, 1),
			},
		});
		cols.push({
			label: 'AKUMA Prime Abwahlquote',
			path: `primeAbwahlquote.ze`,
			sortBy: `primeAbwahlquote.ze`,
			cellModifier: 'bg-yellow',
			labelModifier: 'bg-yellow',
			render(row) {
				return simpleFitCell(row.data.primeAbwahlquote);
			},
			agg: {
				aggType: AggregrationType.avg,
				format: (val) => formatPercentWithIcon(val, 1),
			},
		});
		cols.push({
			label: 'AKUMA Prime Terminierung Extern',
			path: `primeTerminierungExtern.ze`,
			sortBy: `primeTerminierungExtern.ze`,
			cellModifier: 'bg-yellow',
			labelModifier: 'bg-yellow',
			render(row) {
				return simpleFitCell(row.data.primeTerminierungExtern);
			},
			agg: {
				aggType: AggregrationType.avg,
				format: (val) => formatPercentWithIcon(val, 1),
			},
		});

		tm.sortBy = 'agt.name';
		tm.setCols(cols);

		tm.onRowClick = (row) => {
			const p = routes.BERICHTE.getPath('bestand', 'adz', 'fit', 'akuma', row.data.agt.agtId.toString());
			history.pushWithQuery(p);
		};

		tm.idProperty = 'agtId';
		tm.idType = 'number';

		return tm;
	}, []);

	useEffect(() => {
		agtFitnessUiStore.load().then(() => {
			let items = agtFitnessUiStore.items;
			if (berichteUiiStore.selectedAgt) {
				const agtId = berichteUiiStore.selectedAgt.agtId;
				items = items.filter((i) => i.agt.agtId === agtId);
			} else {
				items = agenturListUiStore.applyFilterOnAgt(items) as AgtFitnessVm[];
			}
			setHasItems(items.length > 0);
			// Store all agency IDs for aggregated chart view
			if (items.length > 0) {
				const agtIds = items.map((item) => item.agt.agtId);
				setFilteredAgtIds(agtIds);
			} else {
				setFilteredAgtIds([]);
			}
			tm.setRowData(items);
			tm.sort();
		});
	}, [tm, berichteUiiStore.selectedAgt, agenturListUiStore.filterHasChanged, agenturListUiStore, agtFitnessUiStore.items, agtFitnessUiStore]);

	useEffect(() => {
		// das hier ginge auch nicer, via treeNode Model und openByPath
		const lastPathElement = location.pathname.split('/').pop();
		let agtId;
		if (typeof lastPathElement === 'string') {
			agtId = parseInt(lastPathElement);
		}
		if (agtId && !isNaN(agtId)) {
			setSelectedAgtId(agtId);
		} else {
			setSelectedAgtId(undefined);
		}
	}, [location]);

	const onClose = () => {
		const p = routes.BERICHTE.getPath('bestand', 'adz', 'fit', 'akuma');
		history.pushWithQuery(p);
	};

	return (
		<>
			<div className="table-container">
				{!hasItems ? (
					<div className="pad-1rem">Keine Fitnesstrackerdaten gefunden</div>
				) : (
					<>
						{!selectedAgtId && filteredAgtIds.length > 0 && (
							<div className="pad-1rem pad-bottom-2rem is-sticky">
								<AkumaHistoryChart agtIds={filteredAgtIds} />
							</div>
						)}
						{selectedAgtId && <FitnessTrackerGraphics agtId={selectedAgtId} view="akuma" onClose={onClose} />}
						<Table stickyHeader={true} stickyFooter={true} tm={tm}></Table>
					</>
				)}
			</div>
		</>
	);
});
